import { useCallback } from 'react'
import type { Command, UserOption, UUID } from '@/index'
import { CommandType } from '@/index'
import {
  getLatestConversationTurn,
  isNotInteractable,
} from '@/features/events/conversation/state/types'
import { useSessionStateContext } from '@/features/events/conversation/state'
import { EventType } from '@/features/events/shared'
import { useCrypto } from '@/infra/crypto/context'
import {
  buildCommandPayload,
  commandClient,
  encryptCommand,
} from '@/features/events/conversation/controls/api'
import type { BlockingInboxEntry } from '../types'

/**
 * Option index that maps to the "allow once" response of a tool-permission
 * dialogue. Verified: index 1 is "allow once" and never requires custom input
 * (only the final option does).
 */
const ALLOW_ONCE_OPTION_INDEX = 1

export interface UseInboxQuickAllowParams {
  readonly onError?: (error: Error, entry: BlockingInboxEntry) => void
}

export interface UseInboxQuickAllowReturn {
  readonly allowOnce: (entry: BlockingInboxEntry) => Promise<void>
}

/**
 * Lean, session-agnostic submitter for the "allow once" inbox shortcut.
 *
 * Unlike `useCommandSubmitter`, this hook is NOT bound to a single session at
 * call time, so it can serve every card in the inbox. It composes the exact
 * same building blocks the dialog path uses (`buildCommandPayload` →
 * `encryptCommand` → `commandClient.submit`) to guarantee payload parity, then
 * records the command locally so the pending state clears optimistically.
 */
export function useInboxQuickAllow(
  params: UseInboxQuickAllowParams = {},
): UseInboxQuickAllowReturn {
  const { onError } = params
  const { recordCommand } = useSessionStateContext()
  const { encrypt, activeKeyVersion, isReady } = useCrypto()

  const allowOnce = useCallback(
    async (entry: BlockingInboxEntry): Promise<void> => {
      if (entry.bucket !== EventType.TOOL_PERMISSION_REQUEST) return
      // Non-interactable sessions (e.g. lost tmux) can't accept commands —
      // buildCommandPayload would throw. Mirrors hasAllowOnceOption's guard.
      if (isNotInteractable(entry.session)) return

      const allowOnceOption = findAllowOnceOption(entry)
      if (!allowOnceOption) return

      const latestTurn = getLatestConversationTurn(entry.session)
      if (!latestTurn) return

      if (!isReady || activeKeyVersion === null) {
        throw new Error('Encryption is not available. Cannot submit commands without encryption.')
      }

      const command = buildAllowOnceCommand(entry.event.eventId, allowOnceOption)
      const userPromptId = latestTurn.userPromptId

      // Notify + rethrow on any failure. Defined once so the network-error path
      // and the unsuccessful-result path stay consistent — and so neither uses a
      // throw that its own enclosing try would catch.
      const fail = (error: Error): never => {
        onError?.(error, entry)
        throw error
      }

      let result: Awaited<ReturnType<typeof commandClient.submit>>
      try {
        const payload = buildCommandPayload(command, entry.session, userPromptId, null)
        const finalPayload = await encryptCommand(payload, activeKeyVersion, encrypt)
        result = await commandClient.submit(finalPayload)
      } catch (err) {
        return fail(err instanceof Error ? err : new Error('Failed to allow tool permission'))
      }

      if (!result.success) {
        return fail(new Error(result.message))
      }

      recordCommand(entry.session.sessionId, userPromptId, command)
    },
    [recordCommand, encrypt, activeKeyVersion, isReady, onError],
  )

  return { allowOnce }
}

function findAllowOnceOption(entry: BlockingInboxEntry): UserOption | null {
  const options = entry.event.notificationContext?.agentDialogue?.userOptions
  if (!options) return null
  return options.find((option) => option.index === ALLOW_ONCE_OPTION_INDEX) ?? null
}

/**
 * Whether the entry exposes the "allow once" shortcut: it must be a
 * tool-permission request that carries an `index === 1` option. Used by the
 * card to decide whether to render the quick-allow control, mirroring the
 * guard inside `allowOnce` so the two never disagree.
 */
export function hasAllowOnceOption(entry: BlockingInboxEntry): boolean {
  if (entry.bucket !== EventType.TOOL_PERMISSION_REQUEST) return false
  if (isNotInteractable(entry.session)) return false
  return findAllowOnceOption(entry) !== null
}

function buildAllowOnceCommand(refEventId: UUID, option: UserOption): Command {
  return {
    commandType: CommandType.SUBMIT_OPTION,
    userCommandId: crypto.randomUUID(),
    refEventId,
    timestamp: new Date().toISOString(),
    userOption: option,
    userPrompt: null,
    agentMode: null,
    userAnswers: null,
    errorRecovery: null,
  }
}
